// Icon Mapping Constants for Custom Supabase Icons
// Complete mapping from Lucide icons to custom Supabase Storage icons

export const ICON_MAPPING = {
  // Core Learning Icons
  'search': 'icon-search.png',
  'alert-triangle': 'icon-alert-triangle.png',
  'message-square': 'icon-message-square.png',
  'shield': 'icon-shield.png',
  'lightbulb': 'icon-lightbulb.png',
  'brain': 'icon-brain.png',
  'zap': 'icon-zap.png',
  'target': 'icon-target.png',

  // Navigation Icons
  'chevron-left': 'icon-chevron-left.png',
  'chevron-right': 'icon-chevron-right.png',
  'chevron-up': 'icon-chevron-left.png', // Rotated chevron-left
  'chevron-down': 'icon-chevron-right.png', // Rotated chevron-right
  'arrow-right': 'icon-arrow-right.png',
  'arrow-left': 'icon-arrow-left.png',
  'arrow-up-right': 'icon-arrow-up-right.png',

  // UI/Action Icons
  'plane': 'icon-plane.png',
  'users': 'icon-users.png',
  'user': 'icon-user.png',
  'check-circle': 'icon-check-circle.png',
  'check-square': 'icon-check-square.png',
  'x-circle': 'icon-x-circle.png',
  'plus-circle': 'icon-check-circle.png', // Using check-circle for plus
  'clock': 'icon-clock.png',
  'eye': 'icon-eye.png',
  'eye-off': 'icon-eye-off.png',
  'refresh-cw': 'icon-refresh.png',
  'refresh': 'icon-refresh.png',

  // Content/Tool Icons
  'file-text': 'icon-file-text.png',
  'book-open': 'icon-book-open.png',
  'presentation': 'icon-presentation.png',
  'quote': 'icon-quote.png',
  'copy': 'icon-copy.png',
  'download': 'icon-download.png',
  'code': 'icon-code.png',
  'clipboard-check': 'icon-clipboard-check.png',

  // Professional/Business Icons
  'briefcase': 'icon-briefcase.png',
  'building': 'icon-building.png',
  'mail': 'icon-mail.png',
  'calendar': 'icon-calendar.png',
  'dollar-sign': 'icon-dollar-sign.png',
  'trophy': 'icon-trophy.png',
  'star': 'icon-star.png',
  'heart': 'icon-heart.png',

  // Education/Learning Icons
  'graduation-cap': 'icon-graduation-cap.png',
  'school': 'icon-school.png',
  'bar-chart': 'icon-bar-chart.png',
  'bar-chart-3': 'icon-bar-chart.png',
  'trending-up': 'icon-trending-up.png',

  // Utility Icons
  'settings': 'icon-settings.png',
  'help-circle': 'icon-help-circle.png',
  'home': 'icon-home.png',
  'external-link': 'icon-external-link.png',
  'palette': 'icon-palette.png',
  'sparkles': 'icon-sparkles.png',
  'bot': 'icon-bot.png',

  // Common Aliases and Variations
  'message-circle': 'icon-message-square.png',
  'check': 'icon-check-circle.png',
  'x': 'icon-x-circle.png',
  'plus': 'icon-plus.png', // If we have it
  'minus': 'icon-minus.png', // If we have it
  'edit': 'icon-edit.png', // If we have it
  'trash-2': 'icon-trash.png', // If we have it
} as const;

// Emoji to Custom Icon Mapping
export const EMOJI_MAPPING = {
  // Learning/Target Icons
  '🎯': 'icon-target.png',
  '💡': 'icon-lightbulb.png',
  '🚀': 'icon-zap.png',
  '✨': 'icon-sparkles.png',
  '⚡': 'icon-zap.png',

  // Analytics/Data Icons
  '📊': 'icon-bar-chart.png',
  '📈': 'icon-trending-up.png',
  '📉': 'icon-trending-down.png', // If we have it

  // Success/Achievement Icons
  '🎉': 'icon-trophy.png',
  '🏆': 'icon-trophy.png',
  '⭐': 'icon-star.png',
  '🌟': 'icon-star.png',
  '👑': 'icon-crown.png', // If we have it
  '🥇': 'icon-trophy.png',

  // Search/Discovery Icons
  '🔍': 'icon-search.png',
  '🔎': 'icon-search.png',

  // Communication Icons
  '📧': 'icon-mail.png',
  '📨': 'icon-mail.png',
  '💬': 'icon-message-square.png',
  '🗨️': 'icon-message-square.png',

  // Tools/Work Icons
  '🛠️': 'icon-settings.png',
  '⚙️': 'icon-settings.png',
  '🔧': 'icon-settings.png',

  // Education Icons
  '🎓': 'icon-graduation-cap.png',
  '📚': 'icon-book-open.png',
  '📖': 'icon-book-open.png',
  '🏫': 'icon-school.png',

  // Business Icons
  '💼': 'icon-briefcase.png',
  '🏢': 'icon-building.png',
  '💰': 'icon-dollar-sign.png',
  '💵': 'icon-dollar-sign.png',

  // Health/Heart Icons
  '❤️': 'icon-heart.png',
  '💖': 'icon-heart.png',
  '💝': 'icon-heart.png',

  // Time Icons
  '⏰': 'icon-clock.png',
  '🕐': 'icon-clock.png',
  '⏲️': 'icon-clock.png',

  // Gaming/Interactive Icons
  '🎮': 'icon-plane.png', // Using plane for simulator context
  '🎪': 'icon-presentation.png',

  // Art/Design Icons
  '🎨': 'icon-palette.png',
  '🖼️': 'icon-palette.png',
} as const;

export type IconType = keyof typeof ICON_MAPPING;
export type EmojiType = keyof typeof EMOJI_MAPPING;